import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Field,
  makeStyles,
  Text,
} from "@fluentui/react-components";
import React from "react";
import DropdownField from "../../common/DropdownField/DropdownField";
import MapboxMap from "../../common/MapboxMap/MapboxMap";
import MapboxSearchField from "../../common/MapboxSearchField/MapboxSearchField";
import Modal from "../../common/Modal/Modal";
import ToggleField from "../../common/ToggleField/ToggleField";
import CustomerForm from "../../forms/CustomerForm/CustomerForm";
import RouteDetailsCard from "./RouteDetailsCard/RouteDetailsCard";
import useSearch from "./useSearch";
import RenderIf from "../../common/RenderIf/RenderIf";
import { LoadingTypesEnum } from "../../../types/loading";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px",
    width: "100%",
    height: "100%",
    overflowX: "hidden",
    overflowY: "auto",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  actionButton: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  controls: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    padding: "8px 0",
  },
  resultContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  error: {
    color: "red",
  },
  fullWidth: {
    width: "100%",
    minWidth: "0",
  },
});

const OTRSearch = () => {
  const styles = useStyles();
  const {
    error,
    loading,
    isHazmatCertified,
    isNewCustomerProfileLoading,
    isAddCustomerProfileFormOpen,
    accordionOpenItems,
    companyProfileOptions,
    selectedCustomerProfile,
    oeProfileOptions,
    selectedOeProfile,
    loadTypeOptions,
    selectedLoadType,
    isHazmat,
    quoteRequestId,
    routeCostBreakdown,
    onCustomerProfileChange,
    onOperationalExpenseChange,
    onLoadTypeChange,
    onHazmatToggle,
    onLocationChange,
    onGetRouteEstimates,
    onClearForm,
    onCustomerProfileFormSubmit,
    setOfferPrice,
    setUiState,
    isSuccessButtonDisabled,
  } = useSearch();

  return (
    <div className={styles.container}>
      <div className={styles.field}>
        <Field
          className={styles.fullWidth}
          label={
            <div className={styles.actionButton}>
              Company Profile{" "}
              <Button
                appearance="transparent"
                size="small"
                onClick={() =>
                  setUiState({ isAddCustomerProfileFormOpen: true })
                }
              >
                + Add New
              </Button>
            </div>
          }
        >
          <DropdownField
            options={companyProfileOptions}
            selectedValue={selectedCustomerProfile}
            onSelect={onCustomerProfileChange}
            placeholder="Select company profile"
          />
        </Field>
      </div>

      <div className={styles.field}>
        <Field className={styles.fullWidth} label="Load Type">
          <DropdownField
            options={loadTypeOptions}
            selectedValue={selectedLoadType}
            onSelect={onLoadTypeChange}
            placeholder="Select load type"
          />
        </Field>
      </div>

      {isHazmatCertified && (
        <ToggleField
          label="Hazmat"
          checked={isHazmat}
          onChange={onHazmatToggle}
        />
      )}

      <div className={styles.field}>
        <Field
          className={styles.fullWidth}
          label="Operational Expenses Profile"
        >
          <DropdownField
            options={oeProfileOptions}
            selectedValue={selectedOeProfile}
            onSelect={onOperationalExpenseChange}
            placeholder="Select operational expenses profile"
          />
        </Field>
      </div>

      <div className={styles.field}>
        <Field className={styles.fullWidth} label="Empty Trip Location">
          <MapboxSearchField
            placeholder="Search location"
            onSelected={(location) => onLocationChange(location, "empty")}
          />
        </Field>
      </div>

      <div className={styles.field}>
        <Field className={styles.fullWidth} label="Origin Location">
          <MapboxSearchField
            placeholder="Search location"
            onSelected={(location) => onLocationChange(location, "origin")}
          />
        </Field>
      </div>

      <div className={styles.field}>
        <Field className={styles.fullWidth} label="Destination Location">
          <MapboxSearchField
            placeholder="Search location"
            onSelected={(location) => onLocationChange(location, "destination")}
          />
        </Field>
      </div>

      {error && <Text className={styles.error}>{error}</Text>}

      <div className={styles.controls}>
        <Button
          appearance="secondary"
          disabled={loading === "pending"}
          onClick={onClearForm}
        >
          Clear
        </Button>
        <Button
          appearance="primary"
          disabled={isSuccessButtonDisabled}
          onClick={onGetRouteEstimates}
        >
          Search
        </Button>
      </div>

      <Accordion
        collapsible
        openItems={accordionOpenItems}
        onToggle={(_e, data) => {
          setUiState({ accordionOpenItems: data.openItems as string[] });
        }}
      >
        <AccordionItem value="map">
          <AccordionHeader>Route Map</AccordionHeader>
          <AccordionPanel>
            <MapboxMap
              directionCoordinates={routeCostBreakdown?.routeCoordinates}
            />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      <RenderIf
        shouldRender={
          !!quoteRequestId &&
          !!routeCostBreakdown &&
          loading !== LoadingTypesEnum.pending
        }
      >
        <RouteDetailsCard
          quoteResultId={quoteRequestId}
          quote={routeCostBreakdown}
          onHandleOfferPrice={setOfferPrice}
        />
      </RenderIf>

      <Modal open={isAddCustomerProfileFormOpen} modalType="modal">
        <CustomerForm
          onSubmit={(data) => {
            onCustomerProfileFormSubmit(data);
            setUiState({ isAddCustomerProfileFormOpen: false });
          }}
          onCancel={() => {
            setUiState({ isAddCustomerProfileFormOpen: false });
          }}
          saving={isNewCustomerProfileLoading}
        />
      </Modal>
    </div>
  );
};

export default OTRSearch;
